import { useState, useEffect } from 'react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useBulkLocks } from '../hooks/useBulkLocks';
import { useAuth } from '../contexts/AuthContext';
import { LockIndicator } from '../components/permissions/LockIndicator';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Textarea } from '../components/ui/textarea';
import { Receipt, Search, Eye, Loader2, Calendar, Trash2, BarChart3, Send, CheckCircle2, AlertTriangle, CircleDollarSign, Clock, StickyNote } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { format, differenceInCalendarDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ReadOnlyGuard } from '../components/permissions/ReadOnlyGuard';
import { subscribeDataChange } from '../lib/dataBus';
import { listsEqualById } from '../lib/listDiff';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUT_ENVOI = {
    a_envoyer: { label: 'A envoyer', color: 'bg-red-100 text-red-700' },
    envoyee: { label: 'Envoyee', color: 'bg-emerald-100 text-emerald-700' },
};

const STATUT_PAIEMENT = {
    impayee: { label: 'Impayee', color: 'bg-red-100 text-red-700' },
    partiellement_payee: { label: 'Partiellement payee', color: 'bg-amber-100 text-amber-700' },
    payee: { label: 'Payee', color: 'bg-emerald-100 text-emerald-700' },
};

function formatDate(dateStr) {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd/MM/yyyy', { locale: fr }); } catch { return dateStr; }
}

function RemarqueButton({ facture, onSaved }) {
    const [open, setOpen] = useState(false);
    const [val, setVal] = useState(facture.remarque || '');
    const [saving, setSaving] = useState(false);
    const hasRemarque = !!(facture.remarque || '').trim();
    const save = async () => {
        setSaving(true);
        try {
            await axios.patch(`${API}/api/factures/${facture.id}/remarque`, { remarque: val }, { withCredentials: true });
            toast.success('Remarque enregistrée');
            setOpen(false);
            onSaved(facture.id, val);
        } catch { toast.error('Erreur lors de l\'enregistrement'); }
        finally { setSaving(false); }
    };
    return (
        <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setVal(facture.remarque || ''); }}>
            <PopoverTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => e.stopPropagation()}
                    title={hasRemarque ? 'Voir la remarque' : 'Ajouter une remarque'}
                    data-testid={`facture-remarque-${facture.id}`}
                >
                    <StickyNote size={16} className={hasRemarque ? 'text-amber-500' : 'text-slate-300'} />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3" align="end" onClick={(e) => e.stopPropagation()}>
                <p className="text-xs font-bold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <StickyNote size={13} className="text-amber-500" /> Remarque
                </p>
                <Textarea
                    value={val}
                    onChange={e => setVal(e.target.value)}
                    rows={4}
                    placeholder="Ajouter une remarque..."
                    className="text-sm"
                    data-testid={`facture-remarque-input-${facture.id}`}
                />
                <div className="flex justify-end gap-2 mt-2">
                    <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid={`facture-remarque-cancel-${facture.id}`}>Annuler</Button>
                    <Button
                        size="sm"
                        className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                        onClick={save}
                        disabled={saving}
                        data-testid={`facture-remarque-save-${facture.id}`}
                    >
                        {saving ? <Loader2 size={14} className="animate-spin" /> : 'Enregistrer'}
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default function FacturationPage() {
    const [factures, setFactures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState(null); // 'all' | 'impayee' | 'part_payee' | 'payee' | 'urgent' | null (aucun)
    const navigate = useNavigate();
    const { user } = useAuth();

    const fetchFactures = (silent = false) => {
        if (!silent) setLoading(true);
        axios.get(`${API}/api/factures`, { withCredentials: true })
            .then(res => setFactures(prev => listsEqualById(prev, res.data) ? prev : res.data))
            .catch(() => { if (!silent) toast.error('Erreur de chargement'); })
            .finally(() => { if (!silent) setLoading(false); });
    };

    useEffect(() => { fetchFactures(); }, []);

    useAutoRefresh(() => fetchFactures(true));

    // Sync cross-onglets : si un autre onglet modifie une facture (acompte, statut paiement…), refresh
    useEffect(() => {
        const unsub = subscribeDataChange(['facture', 'client'], () => fetchFactures(true));
        return unsub;
    }, []);

    const factureLocks = useBulkLocks('facture', (factures || []).map(f => f.id));

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        if (!window.confirm('Supprimer cette facture ?')) return;
        try {
            await axios.delete(`${API}/api/factures/${id}`, { withCredentials: true });
            toast.success('Facture supprimee');
            fetchFactures();
        } catch { toast.error('Erreur'); }
    };

    const isUrgent = (f) => {
        if (!f.date_demenagement) return false;
        const jours = differenceInCalendarDays(new Date(f.date_demenagement), new Date());
        // Impayée (ni payée ni partiellement payée) et 5 jours ou moins avant le déménagement
        if (f.statut_paiement === 'impayee' && jours >= 0 && jours <= 5) return true;
        // Date déménagement passée et facture non entièrement payée (impayée ou partiellement payée)
        if (f.statut_paiement !== 'payee' && jours < 0) return true;
        return false;
    };
    const urgentCount = factures.filter(isUrgent).length;

    const filtered = factures.filter(f => {
        // Filtre de statut
        if (activeFilter === 'impayee' && f.statut_paiement !== 'impayee') return false;
        if (activeFilter === 'part_payee' && f.statut_paiement !== 'partiellement_payee') return false;
        if (activeFilter === 'payee' && f.statut_paiement !== 'payee') return false;
        if (activeFilter === 'urgent' && !isUrgent(f)) return false;
        // Recherche texte
        if (!search) return true;
        const s = search.toLowerCase();
        const sNum = s.replace(',', '.');
        const prixStr = f.prix_ttc != null ? f.prix_ttc.toFixed(2) : '';
        const soldeStr = f.solde_du != null ? f.solde_du.toFixed(2) : '';
        const acompteStr = (f.prix_ttc != null && f.solde_du != null) ? (f.prix_ttc - f.solde_du).toFixed(2) : '';
        return (f.client_nom || '').toLowerCase().includes(s)
            || (f.numero_facture || '').includes(s)
            || (f.remarque || '').toLowerCase().includes(s)
            || (sNum !== '' && prixStr.includes(sNum))
            || (sNum !== '' && soldeStr.includes(sNum))
            || (sNum !== '' && acompteStr.includes(sNum));
    }).sort((a, b) => (isUrgent(b) ? 1 : 0) - (isUrgent(a) ? 1 : 0));

    const total = factures.length;
    const aEnvoyer = factures.filter(f => f.statut_envoi === 'a_envoyer').length;
    const envoyee = factures.filter(f => f.statut_envoi === 'envoyee').length;
    const impayee = factures.filter(f => f.statut_paiement === 'impayee').length;
    const partPayee = factures.filter(f => f.statut_paiement === 'partiellement_payee').length;
    const payee = factures.filter(f => f.statut_paiement === 'payee').length;

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="facturation-page">
            <ReadOnlyGuard module="facturation">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="facturation-title">
                        Facturation
                    </h1>
                    <p className="mt-1 text-slate-500">Gestion des factures clients</p>
                </div>
                <Button
                    onClick={() => navigate('/facturation/recap')}
                    className="bg-[#2e7cd6] hover:bg-[#2568b5] text-white"
                    data-testid="detail-facturation-btn"
                >
                    <BarChart3 size={16} className="mr-2" /> Détail facturation
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="facturation-stats">
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'all' ? null : 'all')} className={`text-left transition-all ${activeFilter === 'all' ? 'ring-2 ring-slate-400' : 'hover:shadow-md'}`} data-testid="fact-filter-total">
                    <Card className="bg-white rounded-xl border border-slate-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                                <BarChart3 size={20} className="text-slate-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-900" data-testid="fact-stat-total">{total}</p>
                                <p className="text-xs text-slate-500">Total factures</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'impayee' ? null : 'impayee')} className={`text-left transition-all ${activeFilter === 'impayee' ? 'ring-2 ring-red-400' : 'hover:shadow-md'}`} data-testid="fact-filter-impayee">
                    <Card className="bg-white rounded-xl border border-red-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                                <CircleDollarSign size={20} className="text-red-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-red-700" data-testid="fact-stat-impayee">{impayee}</p>
                                <p className="text-xs text-slate-500">Impayée</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'part_payee' ? null : 'part_payee')} className={`text-left transition-all ${activeFilter === 'part_payee' ? 'ring-2 ring-amber-400' : 'hover:shadow-md'}`} data-testid="fact-filter-part-payee">
                    <Card className="bg-white rounded-xl border border-amber-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                                <Clock size={20} className="text-amber-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-amber-700" data-testid="fact-stat-part-payee">{partPayee}</p>
                                <p className="text-xs text-slate-500">Partiellement payée</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'payee' ? null : 'payee')} className={`text-left transition-all ${activeFilter === 'payee' ? 'ring-2 ring-emerald-400' : 'hover:shadow-md'}`} data-testid="fact-filter-payee">
                    <Card className="bg-white rounded-xl border border-emerald-200 shadow-sm h-full">
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                                <CheckCircle2 size={20} className="text-emerald-600" />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-emerald-700" data-testid="fact-stat-payee">{payee}</p>
                                <p className="text-xs text-slate-500">Payée</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
                <button type="button" onClick={() => setActiveFilter(activeFilter === 'urgent' ? null : 'urgent')} className={`text-left transition-all ${activeFilter === 'urgent' ? 'ring-2 ring-red-400' : 'hover:shadow-md'}`} data-testid="fact-filter-urgent">
                    <Card className={`bg-white rounded-xl border shadow-sm h-full ${urgentCount > 0 ? 'border-red-300 bg-red-50/30' : 'border-slate-200'}`}>
                        <CardContent className="p-4 flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${urgentCount > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                                <AlertTriangle size={20} className={urgentCount > 0 ? 'text-red-600' : 'text-slate-400'} />
                            </div>
                            <div>
                                <p className={`text-2xl font-bold ${urgentCount > 0 ? 'text-red-600' : 'text-slate-400'}`} data-testid="fact-stat-urgent">{urgentCount}</p>
                                <p className="text-xs text-slate-500">Urgent</p>
                            </div>
                        </CardContent>
                    </Card>
                </button>
            </div>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6">
                <CardContent className="p-4">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input placeholder="Rechercher (nom, numéro facture, remarque, prix TTC, acompte, solde dû)..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10" data-testid="facturation-search" />
                    </div>
                </CardContent>
            </Card>

            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <Receipt size={20} className="text-[#2e7cd6]" />
                        Liste des factures ({filtered.length})
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" /></div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">Aucune facture</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table className="min-w-[900px]" data-testid="facturation-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Client</TableHead>
                                        <TableHead>Date demenagement</TableHead>
                                        <TableHead>Statut envoi</TableHead>
                                        <TableHead>Prix TTC</TableHead>
                                        <TableHead>Acompte</TableHead>
                                        <TableHead>Solde du</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {filtered.map(f => {
                                        const envInfo = STATUT_ENVOI[f.statut_envoi] || STATUT_ENVOI.a_envoyer;
                                        const paiInfo = STATUT_PAIEMENT[f.statut_paiement] || STATUT_PAIEMENT.impayee;
                                        return (
                                            <TableRow key={f.id} className={`cursor-pointer hover:bg-slate-50 ${isUrgent(f) ? 'bg-red-50/50' : ''}`} onClick={() => navigate(`/facturation/${f.id}`)} data-testid={`facture-row-${f.id}`}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center shrink-0">
                                                            <Receipt size={20} className="text-emerald-600" />
                                                        </div>
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="min-w-0">
                                                                <p className="font-medium text-slate-900 truncate flex items-center gap-2">
                                                                    {f.client_nom || '-'}
                                                                    <LockIndicator lock={factureLocks[f.id]} />
                                                                </p>
                                                                {f.description && <p className="text-xs text-[#2e7cd6] mt-0.5 truncate">{f.description}</p>}
                                                            </div>
                                                            {isUrgent(f) && (
                                                                <Badge className="bg-red-500 text-white border-0 text-[10px] px-1.5 py-0 h-5 animate-pulse" data-testid={`facture-urgent-${f.id}`}>
                                                                    URGENT
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1.5">
                                                        <Calendar size={12} className="text-slate-400" />
                                                        <span className={`text-sm ${f.is_programme ? 'text-slate-700' : 'text-slate-400 italic'}`}>{f.is_programme ? formatDate(f.date_demenagement) : 'Non programmé'}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-xs ${envInfo.color}`}>{envInfo.label}</Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm font-medium text-slate-800">{f.prix_ttc != null ? `${f.prix_ttc.toFixed(2)} EUR` : '-'}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-sm font-medium text-emerald-700" data-testid={`facture-acompte-${f.id}`}>{(f.prix_ttc != null && f.solde_du != null) ? `${(f.prix_ttc - f.solde_du).toFixed(2)} EUR` : '-'}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className={`text-sm font-medium ${f.solde_du < 0 ? 'text-blue-600' : 'text-slate-800'}`}>{f.solde_du != null ? `${f.solde_du.toFixed(2)} EUR` : '-'}</span>
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant="outline" className={`text-xs ${paiInfo.color}`}>{paiInfo.label}</Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex items-center justify-end gap-1">
                                                        <RemarqueButton
                                                            facture={f}
                                                            onSaved={(id, remarque) => setFactures(prev => prev.map(x => x.id === id ? { ...x, remarque } : x))}
                                                        />
                                                        <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/facturation/${f.id}`); }} data-testid={`facture-view-${f.id}`}>
                                                            <Eye size={16} className="text-slate-600" />
                                                        </Button>
                                                        {user?.role === 'admin' && (
                                                            <Button variant="ghost" size="icon" onClick={(e) => handleDelete(e, f.id)} data-testid={`facture-delete-${f.id}`} title="Supprimer (admin)">
                                                                <Trash2 size={16} className="text-red-500" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>
            </ReadOnlyGuard>
        </div>
    );
}
